import { Grid, Typography } from '@mui/material';
import { BasicButton, SubmitButton } from '@tassat/button';
import { OrderSide } from '@tassat/common-definitions';
import { CurrencyField, SelectField, TextField } from '@tassat/formik-input';
import { Asset } from '@tassat/fred-definitions';
import {
    addSafe,
    convertToNumber,
    formatAsAsset, formatAsAssetNoSign, formatAsCurrency, formatEpochDate,
    multiplySafe
} from '@tassat/utility';
import * as React from 'react';

import { IFormProps } from './types';
import { emptySelectOption } from '../helpers/constant';

export const TransactFormContents = (props: IFormProps) => {
    const {
        classes,
        values,
        errors,
        submitForm,
        isSubmitting,
        isValid,
        transactType,
        orderSide,
        formProps,
        validateForm,
        setFieldValue,
        realTimePrice,
        navPrice,
        currentTab
    } = props;

    const {
        assetTypes,
        accountPositionCcyBalance,
        accountPositionSecurityBalance,
        assetPair,
        feeAmount,
        entitledAssetsByType,
        onAssetChange
    } = formProps;

    const [ selectedAssetType, setSelectedAssetType ] = React.useState<Asset | undefined>(undefined);

    React.useEffect(() => {
        const allEntitledAssets = Object.values(entitledAssetsByType)
            .flat();

        setSelectedAssetType(allEntitledAssets.find(asset => asset.AssetId === values.AssetType.value));
        onAssetChange?.(values.AssetType.value.toString());
    }, [ values.AssetType.value, entitledAssetsByType ]);

    React.useEffect(() => {
        const isSelectionEntitled = assetTypes.some(option => option.value === values.AssetType.value);

        if (!isSelectionEntitled) {
            setFieldValue('AssetType', assetTypes[0] ?? emptySelectOption);
        }
    }, [assetTypes]);

    React.useEffect(() => {
        setFieldValue('Amount', '');
    }, [currentTab]);

    const getFee = async () => {
        if (values.Amount && values.AssetType.value && values.Type) {
            try {
                const coreResponse = await feeAmount(values);

                setFieldValue('Fee', coreResponse.Amount);
            } catch (error) {
                return 'Could not validate, please try again';
            }
        }

        return {};
    };

    const capitalizedTransactType = transactType.charAt(0)
        .toUpperCase() + transactType.slice(1);

    React.useEffect(() => {
        values.Type = orderSide;
    }, [ orderSide, values ]);

    const showCurrency = React.useMemo(() => {
        return OrderSide.SUBSCRIPTION === orderSide || OrderSide.BUY === orderSide;
    }, [orderSide]);

    React.useEffect(() => {
        getFee();
        setFieldValue('AmountWithFee', showCurrency ? (values.Amount ? formatAsAsset(addSafe(convertToNumber(values.Amount), convertToNumber(values.Fee)), assetPair.currency, 'symbol') : 0)
            : (values.Amount ? formatAsAsset(convertToNumber(values.Amount) - convertToNumber(formatAsAssetNoSign(values.Fee, assetPair.currency)), assetPair.currency, 'symbol') : 0));
    }, [ values.Amount, values.AssetType.value, assetPair.currency, values.Type ]);

    React.useEffect(() => {
        setFieldValue('AmountWithFee', showCurrency ? (values.Amount ? formatAsAsset(addSafe(convertToNumber(values.Amount), convertToNumber(values.Fee)), assetPair.currency, 'symbol') : 0)
            : (values.Amount ? formatAsAsset(convertToNumber(values.Amount) - convertToNumber(values.Fee), assetPair.currency, 'symbol') : 0));
    }, [ values.Amount, values.Fee, assetPair.currency, showCurrency ]);

    React.useEffect(() => {
        validateForm();
    }, [accountPositionCcyBalance?.availableBalance]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                className={classes.assetContainer}
                container
                direction='row'
                item
                justifyContent='space-around'
                sm={12}
            >
                <Grid
                    item
                    md={5}
                    sm={12}
                >
                    <SelectField
                        autoComplete
                        errors={errors}
                        label='Select Asset'
                        name='AssetType'
                        options={assetTypes}
                        values={values}
                    />
                </Grid>
                <Grid
                    item
                    md={5}
                    sm={12}
                >
                    {values.AssetType.value !== '' &&

                        <label
                            className={classes.inputLabel}
                        >
                            Pricing
                        </label>}
                    {values.AssetType.value !== '' && ((OrderSide.BUY === orderSide || OrderSide.SELL === orderSide) ?
                        (
                            <>
                                <Typography
                                    className={classes.priceDisplay}
                                >
                                    Real Time Price at <span className={classes.priceValue}>{formatAsCurrency(realTimePrice?.Price)}</span>
                                </Typography>
                                <Typography> as of {`${formatEpochDate(realTimePrice?.AuditTime, 'MMM DD YYYY')} at ${formatEpochDate(realTimePrice?.AuditTime, 'HH:mm:ss')} ET`}</Typography>
                            </>
                        )
                        : (
                            <>
                                <Typography
                                    className={classes.priceDisplay}
                                >
                                    NAV at <span className={classes.priceValue}>{formatAsCurrency(navPrice?.Price)}</span>
                                </Typography>
                                <Typography> as of {`${formatEpochDate(navPrice?.AuditTime, 'MMM DD YYYY')} at ${formatEpochDate(navPrice?.AuditTime, 'HH:mm:ss')} ET`}</Typography>
                            </>
                        )
                    )}

                </Grid>
            </Grid>
            <Grid
                container
                direction='row'
                justifyContent='space-between'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={classes.fieldMargin}
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={12}
                >
                    <Grid
                        className={classes.fieldContainer}
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={12}
                    >
                        <Grid
                            container
                            direction='row'
                            item
                            justifyContent='flex-end'
                            spacing={0}
                            wrap='nowrap'
                        >
                            {(OrderSide.SUBSCRIPTION === orderSide || OrderSide.REDEEM === orderSide) &&
                                <BasicButton
                                    className={(OrderSide.SUBSCRIPTION === orderSide) ? classes.greenButton : classes.redButton}
                                    onClick={() => {
                                    setFieldValue('Amount', (OrderSide.SUBSCRIPTION === orderSide) ?
                                        formatAsAsset(
                                            accountPositionCcyBalance?.availableBalance,
                                            assetPair.currency,
                                            'symbol'
                                        )
                                        : formatAsAsset(
                                            accountPositionSecurityBalance?.availableBalance,
                                            {
                                                Curcode: selectedAssetType?.Curcode ?? '',
                                                Symbol: selectedAssetType?.Symbol ?? '',
                                                Ticker: selectedAssetType?.Ticker ?? '',
                                                DecimalPoint: assetPair.currency?.DecimalPoint ?? 2
                                            },
                                            'ticker',
                                            false,
                                            true
                                        ));
                                }}
                                    text={`${(OrderSide.SUBSCRIPTION === orderSide) ? 'Subscribe' : 'Redeem'} MAX`}
                                    type='submit'
                                />}
                        </Grid>
                        <Grid
                            container
                            direction='row'
                            item
                            spacing={0}
                            wrap='nowrap'
                        >
                            <CurrencyField
                                currencyDecimalPoint={assetPair.currency?.DecimalPoint || 2}
                                currencySymbol={(OrderSide.SUBSCRIPTION === orderSide || OrderSide.BUY === orderSide) ?
                                    assetPair.currency?.Symbol : selectedAssetType?.Ticker}
                                errors={errors}
                                fieldSize='large'
                                label={`Enter ${capitalizedTransactType} Order Details`}
                                name='Amount'
                                placeholder={(OrderSide.SUBSCRIPTION === orderSide || OrderSide.BUY === orderSide) ?
                                    assetPair.currency?.Curcode : selectedAssetType?.Ticker}
                            />
                        </Grid>
                        <Typography
                            className={classes.margin}
                            variant='body2'
                        >
                            {`Fee Amount: ${formatAsAsset(values?.Fee, assetPair.currency, 'symbol')}`}
                        </Typography>
                        {showCurrency ? (
                            accountPositionCcyBalance && assetPair.currency && (
                                <Typography variant='body2'>
                                    {`Available ${assetPair.currency.Ticker} Balance: ${formatAsAsset(accountPositionCcyBalance.availableBalance, assetPair.currency, 'symbol')}`}
                                </Typography>
                            )
                        ) : (
                            <Typography variant='body2'>
                                {`Available ${selectedAssetType?.Ticker} Position: ${formatAsAssetNoSign(accountPositionSecurityBalance?.availableBalance, selectedAssetType, true)} \u2248 ${formatAsAsset((accountPositionSecurityBalance ?
                                    (multiplySafe(Number(accountPositionSecurityBalance?.availableBalance), Number(realTimePrice?.Price))) : 0), assetPair.currency, 'symbol')}`}
                            </Typography>
                        )}
                        <Grid
                            container
                            direction='row'
                            spacing={0}
                            wrap='wrap'
                        >
                            {OrderSide.SUBSCRIPTION === orderSide &&
                                <Typography className={classes.inputLabelLarge}>
                                    Total Estimated Subscription Amount:
                                </Typography>}
                            {OrderSide.BUY === orderSide &&
                                <Typography className={classes.inputLabelLarge}>
                                    Total Estimated Purchase Amount:
                                </Typography>}
                            {(OrderSide.SUBSCRIPTION === orderSide || OrderSide.BUY === orderSide) &&
                                <Typography className={classes.currencyInputLarge}>
                                    {(values.Amount ? formatAsAsset(addSafe(convertToNumber(values.Amount), convertToNumber(values.Fee)), assetPair.currency, 'symbol') : 'USD + Fees')}
                                </Typography>}
                            {(OrderSide.REDEEM === orderSide || OrderSide.SELL === orderSide) &&
                                <Typography className={classes.inputLabelLarge}>
                                    Total Estimated Proceeds:
                                </Typography>}
                            {OrderSide.REDEEM === orderSide &&
                                <Typography className={classes.currencyInputLarge}>
                                    {(values.Amount ? formatAsAsset(multiplySafe(convertToNumber(values.Amount), Number(navPrice?.Price)) - convertToNumber(values.Fee), assetPair.currency, 'symbol') : 'USD - Fees')}
                                </Typography>}
                            {OrderSide.SELL === orderSide &&
                                <Typography className={classes.currencyInputLarge}>
                                    {(values.Amount && realTimePrice?.Price ? formatAsAsset(multiplySafe(convertToNumber(values.Amount), convertToNumber(realTimePrice.Price)) - convertToNumber(values.Fee), assetPair.currency, 'symbol') : 'USD - Fees')}
                                </Typography>}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    className={classes.fieldMargin}
                    container
                    direction='column'
                    item
                    spacing={0}
                    wrap='nowrap'
                    xs={12}
                >
                    <Grid
                        className={classes.fieldContainer}
                        item
                    >
                        <TextField
                            errors={errors}
                            fieldSize='large'
                            label='Private Description (Optional)'
                            maxLength={40}
                            name='PrivateDescription'
                            values={values}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                container
                direction='row'
                item
                justifyContent='center'
                spacing={0}
                wrap='nowrap'
                xs={12}
            >
                <SubmitButton
                    disabled={isSubmitting || !isValid}
                    onClick={() => {
                        submitForm();
                    }}
                    tokenName={transactType === 'subscription' || transactType === 'redemption' ? '' : 'TFND'}
                    type={`${transactType}-token`}
                />
            </Grid>
            <Grid
                container
                direction='row'
                item
                justifyContent='center'
                spacing={0}
                wrap='nowrap'
                xs={12}
            >
                {OrderSide.SUBSCRIPTION === orderSide && selectedAssetType?.Ticker !== 'FTIXX' &&
                    <Typography className={classes.legalText}>
                        {`By pushing SUBSCRIBE above, I hereby certify that the information previously provided to ${selectedAssetType?.Description} in my Subscription Agreement (${selectedAssetType?.Ticker}), AML/KYC questionnaire and/or other documents provided to the ${selectedAssetType?.Ticker}, as well as all account information, agreements and representations provided to tZERO Securities, LLC, remains accurate and complete in all respects as of the date hereof. Click here to access `}
                        <a
                            href='https://storage.googleapis.com/lynq-docs/pdfs/arca_tfnd_ppm.pdf'
                            rel='noreferrer'
                            target='_blank'
                        >
                            {'TFND\'s Private Placement Memorandum and Financial Statements.'}
                        </a>
                    </Typography>}
                {OrderSide.BUY === orderSide && selectedAssetType?.Ticker !== 'FTIXX' &&
                    <Typography className={classes.legalText}>
                        {`By pushing BUY above, I hereby certify that the information previously provided to ${selectedAssetType?.Description} in my Buy Agreement (${selectedAssetType?.Ticker}), AML/KYC questionnaire and/or other documents provided to the ${selectedAssetType?.Ticker}, as well as all account information, agreements and representations provided to tZERO Securities, LLC, remains accurate and complete in all respects as of the date hereof. Click here to access `}
                        <a
                            href='https://storage.googleapis.com/lynq-docs/pdfs/arca_tfnd_ppm.pdf'
                            rel='noreferrer'
                            target='_blank'
                        >
                            {'TFND\'s Private Placement Memorandum and Financial Statements.'}
                        </a>
                    </Typography>}
            </Grid>
        </form>
    );
};